// Best-effort viewport screenshot for the talent platform's debug mode — the
// talent-side twin of the buyer app's `web/src/lib/debug-screenshot.ts`. When a
// tester logs a debug note (red "Debug" toggle ON) we capture what they were
// looking at with html2canvas and attach it to the note. Capture is best-effort
// and bounded: any failure or a timeout resolves to `null` so the note still
// saves (doctrine 1.5 — never throw, degrade). The caller "waits for the shot"
// only on the happy path.

import html2canvas from "html2canvas";

const CAPTURE_TIMEOUT_MS = 6000;
// JPEG keeps the upload small (a typical viewport lands well under the server's
// cap); scale 1 keeps it readable for spotting visual bugs.
const JPEG_QUALITY = 0.7;

// html2canvas 1.x reimplements CSS parsing and THROWS on color functions it
// doesn't know — "Attempting to parse an unsupported color function". Modern
// Chrome serializes `color-mix(...)` tokens (and any oklch/oklab/lab/lch) into
// the CSS Color-4 `color(srgb …)` / `oklab(…)` forms in *computed* style, so the
// moment the clone contains one such value the WHOLE capture rejects and the
// note saves with no shot. The shared `--rc-*` token tiers can carry such
// functions, so — exactly as the buyer app does — resolve every unsupported
// computed color to plain `rgba()` (which html2canvas parses fine) in the
// `onclone` hook before it renders.
const UNSUPPORTED_COLOR_FN = /(?:color|oklab|oklch|lab|lch)\([^()]*\)/g;
const HAS_UNSUPPORTED_COLOR_FN = /(?:color|oklab|oklch|lab|lch)\(/;
// Single-color properties: the whole value is one color.
const COLOR_PROPS = [
  "color",
  "background-color",
  "border-top-color",
  "border-right-color",
  "border-bottom-color",
  "border-left-color",
  "outline-color",
  "text-decoration-color",
  "column-rule-color",
  "caret-color",
  "fill",
  "stroke",
];
// Compound properties: a color may be embedded among offsets/gradient stops, so
// we rewrite each color-function occurrence in place.
const COMPOUND_COLOR_PROPS = ["box-shadow", "background-image"];

/**
 * Replace each unsupported color-function occurrence in `value` using `toRgba`
 * (which converts one `color(…)`/`oklch(…)`/… token to `rgba(…)`). Pure — the
 * rewriting logic is unit-tested with a stub `toRgba`; the real conversion is
 * the browser rasterizer below. Tokens left unmatched by `toRgba` are kept.
 */
export function replaceUnsupportedColors(value: string, toRgba: (fn: string) => string): string {
  return value.replace(UNSUPPORTED_COLOR_FN, (fn) => toRgba(fn));
}

/**
 * Build a resolver that turns any CSS color string — including the wide-gamut
 * `color()`/`oklab()`/`oklch()`/`lab()`/`lch()` functions — into an sRGB
 * `rgba(…)` html2canvas can parse. It rasterizes the color into a 1×1 canvas and
 * reads back the pixel (the only reliable browser-native down-conversion: the
 * `fillStyle` getter and `getComputedStyle` both preserve the wide-gamut form).
 * The 1×1 canvas only ever paints solid colors, so it never taints.
 */
function makeColorResolver(): (value: string) => string {
  const ctx = document.createElement("canvas").getContext("2d");
  return (value: string): string => {
    if (!ctx) return value;
    return replaceUnsupportedColors(value, (fn) => {
      try {
        ctx.clearRect(0, 0, 1, 1);
        ctx.fillStyle = "#000";
        ctx.fillStyle = fn; // ignored if genuinely unparseable → stays #000
        ctx.fillRect(0, 0, 1, 1);
        const [r, g, b, a] = ctx.getImageData(0, 0, 1, 1).data;
        return `rgba(${r}, ${g}, ${b}, ${(a / 255).toFixed(4)})`;
      } catch {
        return fn;
      }
    });
  };
}

/**
 * Walk the cloned document and replace any computed color that uses a function
 * html2canvas can't parse with an equivalent `rgba(…)`, set inline (with
 * `important` so it wins over the stylesheet). Best-effort: never throws.
 */
function resolveUnsupportedColors(doc: Document): void {
  const view = doc.defaultView ?? window;
  const resolve = makeColorResolver();
  doc.querySelectorAll<HTMLElement>("*").forEach((el) => {
    const cs = view.getComputedStyle(el);
    for (const prop of COLOR_PROPS) {
      const v = cs.getPropertyValue(prop);
      if (v && HAS_UNSUPPORTED_COLOR_FN.test(v)) {
        el.style.setProperty(prop, resolve(v), "important");
      }
    }
    for (const prop of COMPOUND_COLOR_PROPS) {
      const v = cs.getPropertyValue(prop);
      if (v && HAS_UNSUPPORTED_COLOR_FN.test(v)) {
        el.style.setProperty(prop, resolve(v), "important");
      }
    }
  });
}

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("screenshot timed out")), ms);
    p.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

/**
 * Capture the visible viewport (what the tester currently sees) as a JPEG Blob,
 * or `null` if capture fails or times out. Never throws.
 */
export async function captureViewport(): Promise<Blob | null> {
  try {
    const canvas = await withTimeout(
      html2canvas(document.body, {
        useCORS: true,
        logging: false,
        scale: 1,
        // Capture only the on-screen region (not the full scroll height).
        x: window.scrollX,
        y: window.scrollY,
        width: window.innerWidth,
        height: window.innerHeight,
        windowWidth: document.documentElement.clientWidth,
        windowHeight: document.documentElement.clientHeight,
        // Down-convert color-mix()/oklch/etc. in the clone so html2canvas 1.x
        // doesn't throw "unsupported color function" and abort the whole shot.
        onclone: (cloned: Document) => {
          try {
            resolveUnsupportedColors(cloned);
          } catch (err) {
            console.warn("debug screenshot color resolve failed", err);
          }
        },
      }),
      CAPTURE_TIMEOUT_MS,
    );
    return await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((blob) => resolve(blob), "image/jpeg", JPEG_QUALITY);
    });
  } catch (err) {
    // Degrade quietly: the note will save without an image.
    console.warn("debug screenshot capture failed", err);
    return null;
  }
}
